import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { addGuestRequest } from "@/lib/podcastStorage";
import { trackLead } from "@/lib/pixel";
import {
  TRACKING_ID,
  LOCATION_ID,
  PROJECT_ID,
  postTrackingEvent,
} from "@/lib/podcastTracking";
import {
  PodcastGuestFormFields,
  PodcastGuestFormData,
  emptyGuestFormData,
} from "@/components/PodcastGuestFormFields";

export const PodcastGuestForm: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] =
    useState<PodcastGuestFormData>(emptyGuestFormData());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Save the guest request FIRST so it always lands in the admin queue,
    // even if the downstream tracking calls throw.
    addGuestRequest({
      fullName: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      instagram: formData.instagram,
      facebook: formData.facebook,
      tiktok: formData.tiktok,
      otherSocial: formData.otherSocial,
      websiteLink: formData.websiteLink,
      testimony: formData.testimony,
      promoting: formData.promoting,
      promotionDetails: formData.promotionDetails,
      beenGuestBefore: formData.beenGuestBefore,
      pastGuestLink: formData.pastGuestLink,
      permissionToContact: formData.permissionToContact,
      acknowledgment: formData.acknowledgment,
    });

    // Tracking is best-effort: never let it prevent the success state.
    try {
      const trackingPayload = {
        type: "external form_submission" as const,
        timestamp: Date.now(),
        formId: "podcast-guest-application",
        formData: {
          first_name: formData.fullName.split(" ")[0],
          last_name: formData.fullName.split(" ").slice(1).join(" ") || "",
          email: formData.email,
          phone: formData.phone,
          website: formData.websiteLink,
        },
        formLabels: {
          first_name: "First Name",
          last_name: "Last Name",
          email: "Email Address",
          phone: "Phone Number",
          website: "Website",
        },
        url: window.location.href,
        title: document.title,
        path: window.location.pathname,
        userAgent: navigator.userAgent,
        trackingId: TRACKING_ID,
        locationId: LOCATION_ID,
        projectId: PROJECT_ID,
        sessionId:
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        properties: {
          deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent)
            ? "mobile"
            : "desktop",
          source: "ai_studio",
          projectId: PROJECT_ID,
          formName: "Podcast Guest Application",
          tags: ["Podcast Guest Applicant"],
        },
      };

      postTrackingEvent(trackingPayload, {
        customFields: {
          Zz1HhaMb4xBwoA5LvLRE: {
            value: formData.instagram,
            label: "Instagram Handle",
          },
          eSxDayPjmIio6t6wPPcC: {
            value: formData.facebook,
            label: "Facebook Handle",
          },
          zatEYP9t9DP8OFNEZwGE: {
            value: formData.tiktok,
            label: "TikTok Handle",
          },
          DZLnVbHger1x4Ipr79PU: {
            value: formData.otherSocial,
            label: "Other Social Handle",
          },
          oxMesFmrfh8MOfmXREwy: {
            value: formData.testimony,
            label: "Testimony",
          },
          FSDc9MhI4EzsgvdPuvCe: {
            value: formData.promoting
              ? formData.promoting.charAt(0).toUpperCase() +
                formData.promoting.slice(1)
              : "",
            label: "Planning To Promote",
          },
          h7yUy9Ec2mOnB2ty623K: {
            value: formData.promotionDetails,
            label: "Promotion Details",
          },
          rMcQcp79ZQmHC5LqmKpM: {
            value: formData.beenGuestBefore
              ? formData.beenGuestBefore.charAt(0).toUpperCase() +
                formData.beenGuestBefore.slice(1)
              : "",
            label: "Been A Guest Before",
          },
          ppq3V7I3jwM01Yma9OoQ: {
            value: formData.pastGuestLink,
            label: "Past Guest Link",
          },
          wnBI9AuoJINECxW6hQuR: {
            value: formData.permissionToContact ? "Yes" : "No",
            label: "Permission To Contact",
          },
          H6C79PX6xQwU7OwmJSKL: {
            value: formData.acknowledgment ? "Acknowledged" : "",
            label: "Application Acknowledgment",
          },
        },
      });

      trackLead({
        content_name: "Podcast Guest Application",
        content_category: "Podcast",
        status: "submitted",
      });
    } catch {
      // Tracking failure must never block the user experience or the
      // success state. The guest request is already saved above.
    }

    setSubmitted(true);
  };

  return (
    <section className="bg-[#FBF1F4] py-16 md:py-24 px-4 border-t border-[#E8DEC8]">
      <div className="max-w-3xl mx-auto text-center space-y-4 mb-10">
        <p className="text-xs font-semibold text-[#9E1B50] uppercase tracking-widest italic">
          Be a Guest
        </p>
        <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E]">
          Got a testimony you've been sitting on?
        </h2>
        <p className="text-sm md:text-base text-[#380C1E]/80 font-light leading-relaxed max-w-2xl mx-auto">
          The whole show is built on one belief: you are as sick as your
          secrets, and somebody's freedom is on the other side of your story. If
          God brought you through something and you're ready to talk about it,
          Ieshia would love to hear from you.
        </p>
      </div>

      <div className="max-w-2xl mx-auto bg-white rounded-3xl p-6 sm:p-10 shadow-lg border border-[#E8DEC8]">
        {submitted ? (
          <div className="text-center py-8 space-y-4">
            <div className="w-14 h-14 bg-[#9E1B50]/10 text-[#9E1B50] rounded-full flex items-center justify-center mx-auto text-2xl">
              ✓
            </div>
            <h3 className="font-serif text-2xl text-[#380C1E]">
              Thank you for sharing your story!
            </h3>
            <p className="text-[#380C1E]/80 text-sm max-w-md mx-auto">
              We have received your testimony introduction. Ieshia and her team
              review every story with prayer and privacy.
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setFormData(emptyGuestFormData());
              }}
              className="bg-[#9E1B50] hover:bg-[#801340] text-white rounded-full px-6 py-2 text-xs font-bold uppercase tracking-wider mt-4"
            >
              Submit another testimony
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <PodcastGuestFormFields
              formData={formData}
              setFormData={setFormData}
            />
            <button
              type="submit"
              className="w-full bg-[#9E1B50] hover:bg-[#801340] text-white font-bold uppercase text-[11px] tracking-wider py-4 rounded-full transition-all shadow-md"
            >
              Start the Conversation →
            </button>
          </form>
        )}
      </div>

      <div className="mt-8 text-center">
        <p className="text-xs text-[#380C1E]/70">
          Podcast host looking to have Ieshia on YOUR show?{" "}
          <a
            href="/book"
            className="text-[#9E1B50] font-semibold underline hover:text-[#801340]"
          >
            Head to the booking page →
          </a>
        </p>
      </div>
    </section>
  );
};
